/**
 * MonthlyTrendChart (iter 277)
 *
 * Bar chart with numeric data labels on every bar + a dashed 3-month
 * rolling-average trend line. The trend line EXCLUDES the current (in-
 * progress) month per user spec so we're not comparing partial data to
 * fully-completed months.
 *
 * Props:
 *   • data — [{month, value, is_current, ...}]
 *   • label — chart title
 *   • format — 'num' | 'pct'
 *   • color — bar fill
 *   • height — px (default 220)
 *   • yFormatter, tooltipHint — optional custom formatting
 */
import React, { useMemo } from 'react';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  LabelList, CartesianGrid, ReferenceLine,
} from 'recharts';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const shortMonth = (ymd) => {
  if (!ymd) return '';
  const y = ymd.slice(2, 4);
  const m = Number(ymd.slice(5, 7));
  return `${MONTHS[m - 1]} '${y}`;
};

const fmtNum = (v) => (v == null ? '—' : Number(v).toLocaleString());
const fmtPct = (v, d = 0) => (v == null ? '—' : `${(Number(v) * 100).toFixed(d)}%`);

export default function MonthlyTrendChart({
  data,
  title,
  subtitle,
  format = 'num',
  color = '#685B72',
  height = 220,
  yDomain,
  testid,
}) {
  // Compose the plot rows + a 3-month rolling average excluding the
  // current month. The rolling window is anchored on completed months
  // only, so months where the point is the current month or where the
  // window includes the current month are skipped.
  const rows = useMemo(() => {
    const src = data || [];
    // First mark which months are "completed" (non-current).
    return src.map((d, i) => {
      const isCur = d.is_current;
      const window = [];
      for (let j = i - 2; j <= i; j++) {
        if (j < 0) continue;
        const p = src[j];
        if (!p || p.is_current) return { ...d, label: shortMonth(d.month), trend: null };
        if (p.value == null) return { ...d, label: shortMonth(d.month), trend: null };
        window.push(Number(p.value));
      }
      const trend = (!isCur && window.length === 3) ? window.reduce((a, b) => a + b, 0) / 3 : null;
      return { ...d, label: shortMonth(d.month), trend };
    });
  }, [data]);

  if (!rows.length) {
    return (
      <div className="rounded-2xl border p-6 text-center bg-white" style={{ borderColor: '#E9E3DA', color: '#685B72' }} data-testid={testid}>
        <div className="font-serif text-lg mb-1" style={{ color: '#2D2833' }}>{title}</div>
        <div className="text-[12px]">No data in the last 13 months to plot.</div>
      </div>
    );
  }

  const yFmt = format === 'pct' ? (v) => fmtPct(v, 0) : fmtNum;
  const barLabelFmt = format === 'pct' ? (v) => (v == null ? '' : `${(v * 100).toFixed(0)}%`) : (v) => (v == null ? '' : Number(v).toLocaleString());

  return (
    <div className="rounded-2xl border p-4 bg-white" style={{ borderColor: '#E9E3DA' }} data-testid={testid}>
      <div className="flex items-baseline justify-between mb-2 gap-3 flex-wrap">
        <div>
          <div className="text-[9px] uppercase tracking-[0.18em] font-bold" style={{ color: '#685B72' }}>Monthly trend · 13 months</div>
          <h3 className="font-serif text-lg leading-tight" style={{ color: '#2D2833' }}>{title}</h3>
          {subtitle && <div className="text-[11px] mt-0.5" style={{ color: '#685B72' }}>{subtitle}</div>}
        </div>
        <div className="text-[10px]" style={{ color: '#685B72' }}>
          <span className="inline-block w-3 h-1.5 mr-1 rounded" style={{ background: color }} /> Actual
          <span className="ml-3 inline-block w-3 border-b-2 border-dashed align-middle" style={{ borderColor: '#8A3A3A' }} /> 3-mo trend (completed months only)
        </div>
      </div>
      <ResponsiveContainer width="100%" height={height}>
        <ComposedChart data={rows} margin={{ top: 24, right: 12, left: 4, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#F1EDF5" vertical={false} />
          <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#685B72' }} axisLine={false} tickLine={false} />
          <YAxis tickFormatter={yFmt} tick={{ fontSize: 10, fill: '#685B72' }} width={44} axisLine={false} tickLine={false} domain={yDomain} />
          <Tooltip
            formatter={(v, k) => [yFmt(v), k === 'trend' ? '3-mo trend' : 'Actual']}
            labelFormatter={(l) => l}
            contentStyle={{ borderRadius: 8, borderColor: '#E9E3DA', fontSize: 12 }}
          />
          <Bar dataKey="value" fill={color} radius={[4, 4, 0, 0]} maxBarSize={44}>
            <LabelList dataKey="value" position="top" formatter={barLabelFmt} style={{ fill: '#2D2833', fontSize: 10, fontWeight: 600 }} />
          </Bar>
          <Line
            dataKey="trend"
            type="monotone"
            stroke="#8A3A3A"
            strokeWidth={2}
            strokeDasharray="4 3"
            dot={false}
            connectNulls={false}
            isAnimationActive={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
